/**
 * The three browse views over the projects directory: the category-driven
 * `/projects` grid plus the two AI-image-label views, Spaces (room type) and
 * Styles (architectural style).
 *
 * Spaces and Styles read the ImageLabel OpenSearch index rather than the
 * project index, so a labelled photo — not a project — is the unit in the grid.
 * They are views over `/projects`, not entity types of their own: the trailing
 * path segment is what selects them, mirroring how `/products/suppliers` sits
 * over the product index.
 */
export type ProjectBrowseView = 'projects' | 'spaces' | 'styles';

/** The two views served from the ImageLabel index. */
export type ProjectImageLabelView = Exclude<ProjectBrowseView, 'projects'>;

export interface ProjectBrowseViewConfig {
    view: ProjectBrowseView;
    /** Route path for the view's root. */
    path: string;
    /** Tab title in the browse nav. */
    label: string;
    /** Tab sub-label in the browse nav. */
    subLabel: string;
}

/** Tab order matches the Figma browse nav: Projects, Spaces, Styles. */
export const PROJECT_BROWSE_VIEWS: ProjectBrowseViewConfig[] = [
    {
        view: 'projects',
        path: '/projects',
        label: 'Projects',
        subLabel: 'Browse by category',
    },
    {
        view: 'spaces',
        path: '/projects/spaces',
        label: 'Spaces',
        subLabel: 'Browse by room or space',
    },
    {
        view: 'styles',
        path: '/projects/styles',
        label: 'Styles',
        subLabel: 'Browse by style',
    },
];

interface ImageLabelViewConfig {
    /** Route path for the view's root. */
    path: string;
    /** ImageLabel filter key the pills drive, and the URL search param name. */
    filterKey: 'roomTypePrimary' | 'architecturalStyle';
    /** Aggregation requested from searchImageAggregate to build the pills. */
    aggregationKey: 'aggRoomTypePrimary' | 'aggArchitecturalStyle';
    /** Key the aggregation comes back under (the request key minus `agg`). */
    responseKey: 'roomTypePrimary' | 'architecturalStyle';
    /** Leading "All …" pill and back-button label. */
    rootLabel: string;
    /** Grid heading when no pill is selected. */
    sectionTitle: string;
    /** Eyebrow above the intro statement. */
    eyebrow: string;
    /** Intro statement. */
    heading: string;
}

/**
 * Per-view ImageLabel wiring. Spaces browses `RoomTypePrimary` (the room a
 * photo shows); Styles browses `ArchitecturalStyle` (the project-level style).
 * `interiorStyle` stays available as a URL filter on both but is not the pill
 * axis — two style vocabularies in one pill row read as duplicates.
 */
export const IMAGE_LABEL_VIEWS: Record<
    ProjectImageLabelView,
    ImageLabelViewConfig
> = {
    spaces: {
        path: '/projects/spaces',
        filterKey: 'roomTypePrimary',
        aggregationKey: 'aggRoomTypePrimary',
        responseKey: 'roomTypePrimary',
        rootLabel: 'All Spaces',
        sectionTitle: 'All Spaces',
        eyebrow: 'Projects Library',
        heading:
            'Browse by room or space. Every kitchen, bathroom and living room on ArchiPro, labelled from the photography of real projects.',
    },
    styles: {
        path: '/projects/styles',
        filterKey: 'architecturalStyle',
        aggregationKey: 'aggArchitecturalStyle',
        responseKey: 'architecturalStyle',
        rootLabel: 'All Styles',
        sectionTitle: 'All Styles',
        eyebrow: 'Projects Library',
        heading:
            'Browse by style. From coastal to brutalist, explore the architectural language of real projects built here.',
    },
};

/**
 * Id of the leading "All Spaces" / "All Styles" pill. Lives here rather than
 * beside the data loader so the browse page can import it without pulling a
 * `.server` module into the client bundle.
 */
export const ALL_PILL_ID = 0;

/** Path segments that select an ImageLabel view under `/projects`. */
const IMAGE_LABEL_SEGMENTS: Record<string, ProjectImageLabelView> = {
    spaces: 'spaces',
    styles: 'styles',
};

/**
 * Resolve the ImageLabel view a directory `categoryLink` addresses, or
 * undefined when the link is an ordinary project category. Used by the browse
 * pages and by the shared View More endpoint to pick the index to read.
 */
export const resolveImageLabelView = (
    categoryLink: string,
): ProjectImageLabelView | undefined => {
    const [, root, segment] = categoryLink.split('/');
    if (root !== 'projects' || !segment) {
        return undefined;
    }
    return IMAGE_LABEL_SEGMENTS[segment.toLowerCase()];
};

/**
 * Images per grid page. Double the captioned directories' 36: these tiles are a
 * sixth of the width and carry no caption, so a page of 36 is a short wall.
 */
export const IMAGE_LABEL_PAGE_SIZE = 72;

/**
 * Image candidate the walls ask for, matching the three-column /projects grid.
 * Larger than a wall tile needs, but shared with that page's cache and already
 * downloaded by the time a reader opens the preview over it.
 */
export const IMAGE_WALL_WIDTH = '750px';
